import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Heart, 
  Stethoscope, 
  Activity, 
  Eye, 
  Brain, 
  Bone, 
  TestTube, 
  Scan,
  Clock,
  Calendar,
  ArrowRight,
  CheckCircle,
  Shield,
  Award,
  Search,
  Users
} from 'lucide-react';
import { Card, Button, Badge, Input } from '../../components/ui';
import { mockServices } from '../../data/mockData';
import { formatCurrency, searchFilter } from '../../utils';

const ServicesPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Service categories with icons
  const categories = [
    { id: 'all', name: 'All Services', icon: Stethoscope },
    { id: 'General Medicine', name: 'General Medicine', icon: Heart },
    { id: 'Cardiology', name: 'Cardiology', icon: Activity },
    { id: 'Orthopedics', name: 'Orthopedics', icon: Bone },
    { id: 'Dermatology', name: 'Dermatology', icon: Shield },
    { id: 'Preventive Care', name: 'Preventive Care', icon: CheckCircle },
    { id: 'Laboratory', name: 'Laboratory', icon: TestTube },
    { id: 'Imaging', name: 'Imaging', icon: Scan },
  ];

  // Filter services based on category and search
  const filteredServices = searchFilter(
    selectedCategory === 'all' 
      ? mockServices 
      : mockServices.filter(service => service.category === selectedCategory),
    searchTerm,
    ['name', 'description', 'category']
  );

  // Features of our service
  const serviceFeatures = [
    {
      icon: Clock,
      title: 'Flexible Scheduling',
      description: 'Book appointments at your convenience with our easy online booking system'
    },
    {
      icon: Award,
      title: 'Expert Care',
      description: 'Our board-certified physicians provide the highest quality medical care'
    },
    {
      icon: Shield,
      title: 'Safe & Secure',
      description: 'State-of-the-art facilities with the latest safety protocols'
    },
    {
      icon: Heart,
      title: 'Compassionate Service',
      description: 'Patient-centered care with personalized attention to your needs'
    }
  ];

  const getServiceIcon = (category: string) => {
    const iconMap: { [key: string]: any } = {
      'General Medicine': Heart,
      'Cardiology': Activity,
      'Orthopedics': Bone,
      'Dermatology': Shield,
      'Preventive Care': CheckCircle,
      'Laboratory': TestTube,
      'Imaging': Scan,
    };
    return iconMap[category] || Stethoscope;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-800 via-blue-900 to-blue-800 text-white overflow-hidden">
        {/* Medical Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1559757175-0eb30cd8c063?w=1920&h=1080&fit=crop&crop=center"
            alt="Medical Services"
            className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-800/60 via-blue-900/70 to-blue-800/65"></div>
        </div>
        
        {/* Decorative background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full opacity-10 blur-3xl transform translate-x-32 -translate-y-32"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-teal-500 rounded-full opacity-10 blur-3xl transform -translate-x-32 translate-y-32"></div>
        </div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center space-y-8">
            {/* Services Badge */}
            <div className="inline-flex items-center px-6 py-3 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <span className="text-sm font-medium text-white/90">
              Comprehensive Medical Services
              </span>
            </div>
            
            {/* Main Heading */}
            <div className="space-y-4">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight">
                Medical Services{' '}
                <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
                  COMPLETE
                </span>
            </h1>
              <p className="text-lg md:text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
                From routine check-ups to specialized treatments, we offer a complete range of medical services
            </p>
            </div>
            
            {/* CTA Button */}
            <div className="pt-6">
            <Link to="/booking">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold px-8 py-3 rounded-full shadow-xl transform hover:scale-105 transition-all duration-300"
                >
                <Calendar className="w-5 h-5 mr-2" />
                Book Service Now
              </Button>
            </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-gradient-to-br from-slate-50 to-blue-50 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/3 left-0 w-72 h-72 bg-teal-200 rounded-full opacity-20 blur-3xl -translate-x-36"></div>
          <div className="absolute bottom-1/3 right-0 w-72 h-72 bg-cyan-200 rounded-full opacity-20 blur-3xl translate-x-36"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our{' '}
              <span className="bg-gradient-to-r from-teal-500 to-cyan-500 bg-clip-text text-transparent">
                Services
              </span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experience healthcare excellence with our comprehensive approach
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {serviceFeatures.map((feature, index) => (
              <div key={index} className="group">
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                  <div className="text-center">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <feature.icon className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors duration-300">{feature.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Search and Filter */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <div className="inline-flex items-center px-4 py-2 bg-teal-50 rounded-full mb-6">
                <span className="text-sm font-semibold text-teal-700">Browse Services</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Find Your Perfect{' '}
                <span className="bg-gradient-to-r from-teal-500 to-cyan-500 bg-clip-text text-transparent">
                  Treatment
                </span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Browse our comprehensive range of medical services and find the care you need.
              </p>
            </div>

            {/* Search Bar */}
            <div className="max-w-lg mx-auto mb-10">
              <div className="relative">
              <Input
                placeholder="Search services..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 pr-4 py-4 text-lg rounded-2xl border-2 border-gray-200 focus:border-teal-500 transition-colors shadow-lg"
              />
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-gray-400" />
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`group inline-flex items-center px-6 py-3 rounded-2xl font-semibold transition-all duration-300 transform hover:scale-105 ${
                    selectedCategory === category.id
                      ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-xl'
                      : 'bg-white text-gray-700 hover:bg-gray-50 shadow-lg border border-gray-200'
                  }`}
                >
                  <category.icon className={`w-5 h-5 mr-2 ${selectedCategory === category.id ? 'text-white' : 'text-gray-500 group-hover:text-teal-500'}`} />
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {filteredServices.map((service) => {
              const ServiceIcon = getServiceIcon(service.category);
              return (
                <div key={service.id} className="group">
                  <div className="relative bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border border-gray-100 overflow-hidden h-full">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-teal-400 to-cyan-400 opacity-10 rounded-bl-3xl"></div>
                    
                    <div className="relative">
                      <div className="flex items-start justify-between mb-6">
                        <div className="flex items-center space-x-4">
                          <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                            <ServiceIcon className="w-8 h-8 text-white" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-gray-900 group-hover:text-teal-600 transition-colors duration-300">{service.name}</h3>
                            <div className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-teal-100 to-cyan-100 text-teal-700 rounded-full text-sm font-semibold mt-2">
                              {service.category}
                            </div>
                          </div>
                      </div>
                    </div>
                    
                      <p className="text-gray-600 mb-8 leading-relaxed text-base">
                      {service.description}
                    </p>
                    
                      <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-2xl p-6 mb-8">
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600 font-medium flex items-center">
                              <Clock className="w-4 h-4 mr-2 text-teal-500" />
                              Duration:
                            </span>
                            <span className="font-bold text-gray-900">{service.duration} minutes</span>
                      </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600 font-medium">Price:</span>
                            <span className="font-bold text-2xl bg-gradient-to-r from-teal-500 to-cyan-500 bg-clip-text text-transparent">
                          {formatCurrency(service.price)}
                        </span>
                          </div>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <Link to={`/booking?service=${service.id}`} className="block">
                          <Button className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold py-4 rounded-2xl shadow-lg transform hover:scale-105 transition-all duration-300">
                            <Calendar className="w-5 h-5 mr-2" />
                          Book This Service
                        </Button>
                      </Link>
                        <Button className="w-full border-2 border-gray-200 hover:border-teal-300 hover:bg-teal-50 text-gray-700 hover:text-teal-600 font-semibold py-3 rounded-xl transition-all duration-300">
                        Learn More
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* No results message */}
          {filteredServices.length === 0 && (
            <div className="text-center py-12">
              <Stethoscope className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No services found</h3>
              <p className="text-gray-600">
                Try adjusting your search or filter criteria to find the services you're looking for.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Insurance & Payment Section */}
      <section className="py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Insurance & Payment Options
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                We accept most major insurance plans and offer flexible payment options 
                to make healthcare accessible for everyone.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <span className="text-gray-700">Most major insurance plans accepted</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <span className="text-gray-700">Flexible payment plans available</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <span className="text-gray-700">Direct billing to insurance</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <span className="text-gray-700">Transparent pricing with no hidden fees</span>
                </div>
              </div>
              
              <div className="mt-8">
                <Link to="/contact">
                  <Button variant="outline" size="lg">
                    Insurance Questions?
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
            
            <div>
              <Card className="p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Need Help Choosing a Service?
                </h3>
                <p className="text-gray-600 mb-6">
                  Our healthcare coordinators are here to help you find the right 
                  service for your needs and answer any questions about coverage.
                </p>
                <div className="space-y-4">
                  <Link to="/contact">
                    <Button className="w-full">
                      Contact Our Team
                    </Button>
                  </Link>
                  <Link to="/booking">
                    <Button variant="outline" className="w-full">
                      Schedule Consultation
                    </Button>
                  </Link>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative bg-gradient-to-br from-slate-800 via-blue-900 to-blue-800 text-white overflow-hidden">
        {/* Medical Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1559757175-0eb30cd8c063?w=1920&h=1080&fit=crop&crop=center"
            alt="Medical Services"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-800/80 via-blue-900/85 to-blue-800/80"></div>
        </div>

        {/* Decorative background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500 rounded-full opacity-10 blur-3xl transform translate-x-32 -translate-y-32"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-teal-500 rounded-full opacity-10 blur-3xl transform -translate-x-32 translate-y-32"></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="space-y-6">
            {/* Section Badge */}
            <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <span className="text-sm font-medium text-white/90">
            Ready to Get the Care You Need?
              </span>
            </div>

            {/* Main Heading */}
            <div className="space-y-4">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight tracking-tight">
                Don't Wait for Your{' '}
                <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
                  Health
                </span>
          </h2>
              <p className="text-lg md:text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
                Book your appointment today and experience our exceptional medical services with our expert team.
          </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
            <Link to="/booking">
                <Button size="lg" className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold px-8 py-3 rounded-full shadow-xl transform hover:scale-105 transition-all duration-300">
                <Calendar className="w-5 h-5 mr-2" />
                Book Appointment
              </Button>
            </Link>
            <Link to="/doctors">
                <Button variant="outline" size="lg" className="border-2 border-white text-white hover:bg-white hover:text-slate-800 backdrop-blur-sm font-semibold px-8 py-3 rounded-full shadow-lg transform hover:scale-105 transition-all duration-300">
                  <Users className="w-5 h-5 mr-2" />
                Meet Our Doctors
              </Button>
            </Link>
            </div>

            {/* Additional Info */}
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-8 pt-8">
              <div className="flex items-center space-x-2 text-blue-100">
                <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                <span className="text-sm font-medium">Comprehensive Services</span>
              </div>
              <div className="flex items-center space-x-2 text-blue-100">
                <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                <span className="text-sm font-medium">Advanced Technology</span>
              </div>
              <div className="flex items-center space-x-2 text-blue-100">
                <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                <span className="text-sm font-medium">Personalized Care</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;